import * as React from 'react';

import { cn, EntityDetails, Loading, LocalizedDate } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as state from '../../_state/containerDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { tradeRoutePaths as tradeRoutes } from '../../routes';
import { enumContainerStatus } from '../../types/container';

import { ContainerDetailHeaderActions } from './ContainerDetailHeaderActions';
import { ContainerDetailsTimeStamps } from './ContainerDetailsTimeStamps';
import { ContainerTabOverview } from './ContainerTabOverview';
import { ContainerTabTimeline } from './ContainerTabTimeline';
import { ContainerTransitionDialog } from './ContainerTransitionDialog';
import { ContainerVoidDialog } from './ContainerVoidDialog';

import { SalesRootLayout } from '~/modules/trade/_components/_layouts/SalesRootLayout';
import { ContainerStatusBadge } from '~/shared/components/ContainerStatusBadge';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

type ContainerTitleProps = {
  bookingNumber?: string;
  containerNumber: string | null;
};
const ContainerTitle = ({
  containerNumber,
  bookingNumber,
}: ContainerTitleProps) => {
  const t = useTranslations();
  return (
    <>
      {t.containersView.titleDescription(`${containerNumber ?? ''}`)}{' '}
      {bookingNumber ? <>&bull; {bookingNumber}</> : null}
    </>
  );
};

export const ContainerDetailView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const toast = useToast();

  const containerId = useAtomValue(state.$containerId);
  const [activeTab, setActiveTab] = useAtom(state.$activeTab);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useAtom(
    state.$isVoidDialogOpen,
  );
  const [isTransitionDialogOpen, setIsTransitionDialogOpen] = useAtom(
    state.$isTransitionDialogOpen,
  );

  const {
    data: container,
    isFetching,
    loadingState,
    error,
    refetch,
  } = useAtomValue(state.$remoteContainer);

  const voidContainer = useSetAtom(state.$voidContainer);

  const notificationBanner = React.useMemo(() => {
    if (!container) return null;

    if (container.status === enumContainerStatus.Shipped) {
      return {
        label: (
          <div className="flex flex-row items-center gap-1">
            {t.containersView.detail.notifications.shippedOn}{' '}
            <span className="font-bold">
              <LocalizedDate date={container.shippedAt ?? undefined} />
            </span>
          </div>
        ),
        type: 'success' as const,
      };
    }

    if (container.status === enumContainerStatus.Voided) {
      return {
        label: (
          <div className="flex flex-row items-center gap-1">
            {t.containersView.detail.notifications.voidedOn}{' '}
            <span className="font-bold">
              <LocalizedDate date={container.timestamps.updatedAt} />
            </span>
          </div>
        ),
        type: 'failure' as const,
      };
    }

    return null;
  }, [container, t]);

  const handleBack = React.useCallback(() => {
    navigate(tradeRoutes.containers);
  }, [navigate]);

  const handleVoid = React.useCallback(() => {
    setIsVoidDialogOpen(true);
  }, [setIsVoidDialogOpen]);

  const handleVoidConfirm = React.useCallback(
    async (id: string) => {
      try {
        await voidContainer({ id });
        toast.success(t.containersView.messages.voided);
        setIsVoidDialogOpen(false);
        refetch?.();
      } catch (err) {
        toast.error(
          t.containersView.messages.voidError(
            err instanceof Error ? err.message : t.common.unknown,
          ),
        );
      }
    },
    [voidContainer, toast, t, setIsVoidDialogOpen, refetch],
  );

  if (loadingState.isInitialLoading || isFetching) {
    return (
      <SalesRootLayout>
        <div className="p-4">
          <Loading />
        </div>
      </SalesRootLayout>
    );
  }

  if (error || !container) {
    return (
      <SalesRootLayout>
        <div className="p-4 text-failure">
          {t.containersView.detail.error(
            error?.message ?? t.containersView.content.error(''),
          )}
        </div>
      </SalesRootLayout>
    );
  }

  return (
    <SalesRootLayout className={cn('@ContainerDetailView')}>
      <EntityDetails
        onChangeTab={(tab) => setActiveTab(tab)}
        currentTab={activeTab}
        onBackPressed={handleBack}
        primaryTitle={
          <ContainerTitle
            bookingNumber={container.booking?.bookingNumber}
            containerNumber={container.containerNumber}
          />
        }
        subtitle={<ContainerDetailsTimeStamps container={container} />}
        headerActions={
          <ContainerDetailHeaderActions
            containerStatus={container.status}
            onVoid={handleVoid}
            onUpdateStatus={() => setIsTransitionDialogOpen(true)}
          />
        }
        badges={
          <div className="flex flex-row items-center gap-2">
            <ContainerStatusBadge status={container.status} />
          </div>
        }
        notificationBanner={notificationBanner}
        tabs={{
          overview: {
            title: t.containersView.detail.tabs.overview,
            content: () => (
              <ContainerTabOverview container={container} onRefetch={refetch} />
            ),
          },
          timeline: {
            title: t.containersView.detail.tabs.timeline,
            content: () => <ContainerTabTimeline container={container} />,
          },
        }}
      />

      {containerId && isVoidDialogOpen && (
        <ContainerVoidDialog
          containerId={containerId}
          onVoid={handleVoidConfirm}
          onCancel={() => setIsVoidDialogOpen(false)}
        />
      )}

      {containerId && isTransitionDialogOpen && (
        <ContainerTransitionDialog
          container={container}
          onSuccess={() => {
            setIsTransitionDialogOpen(false);
            refetch?.();
          }}
          onCancel={() => setIsTransitionDialogOpen(false)}
        />
      )}
    </SalesRootLayout>
  );
};
